import React, { MutableRefObject } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { Typography, Chip } from 'Components/atoms';
import { RiDeleteBin6Line } from '@react-icons/all-files/ri/RiDeleteBin6Line';
import { ImPencil } from '@react-icons/all-files/im/ImPencil';
import {
    TAGSPECS_COL_NAME,
    TAGSPECS_COL_RID,
    TAGSPECS_COL_DESCRIPTION,
    TAGSPECS_COL_CONFIG,
    TAGSPECS_COL_NOTIFY,
    TAGSPECS_COL_CREATED,
    TAGSPECS_EDIT_TITLE,
    TAGSPECS_DELETE_TITLE,
    TAGSPECS_NO_CONFIG,
    TAGSPECS_EMPTY_CONFIG,
    TAGSPECS_VIEW_CONFIG,
} from 'Core-utils/constants/constants';
import { TagSpecDto } from '@ampsec/platform-client';
import { RuleOption } from 'Core-utils/types/rules.types';
import { isJsony } from 'Core-utils/helpers/ruleBuilderHelpers';

type T = (key: string, fallback?: string) => string;

export function getTagSpecsGridColumns(
    t: T,
    // Refs so cell renderers always read the latest values without requiring useMemo re-creation
    ruleOptionsRef: MutableRefObject<RuleOption[]>,
    connectorMapRef: MutableRefObject<Record<string, string>>,
    onEditClickRef: MutableRefObject<(spec: TagSpecDto) => void>,
    onDeleteClickRef: MutableRefObject<(id: string) => void>,
    onViewConfig: (spec: TagSpecDto) => void,
): ColDef<TagSpecDto>[] {
    return [
        {
            colId: 'name',
            field: 'name',
            headerName: t(TAGSPECS_COL_NAME, 'NAME'),
            sortable: true,
            width: 192,
            minWidth: 110,
            cellRenderer: (params: ICellRendererParams<TagSpecDto>) => (
                <Chip value={params.value ?? ''} variant="status" />
            ),
        },
        {
            colId: 'rid',
            field: 'rid',
            headerName: t(TAGSPECS_COL_RID, 'RULE (RID)'),
            sortable: true,
            width: 224,
            minWidth: 130,
            autoHeight: true,
            cellRenderer: (params: ICellRendererParams<TagSpecDto>) => {
                const row = params.data;
                if (!row?.rid) {
                    return (
                        <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                            —
                        </Typography>
                    );
                }
                const matchedCid = ruleOptionsRef.current.find((r) => r.value === row.rid)?.cid;
                const connLabel = matchedCid
                    ? connectorMapRef.current[matchedCid] ?? matchedCid
                    : null;
                return (
                    <div className="d-flex flex-column gap-1 my-2">
                        <Typography
                            variant="body5"
                            className="text-primary-500 bg-dark px-2 py-1 rounded tagspecs-config__rid-code"
                        >
                            {row.rid}
                        </Typography>
                        {connLabel && (
                            <Typography variant="caption1" color="text-low-emphasis">
                                {connLabel}
                            </Typography>
                        )}
                    </div>
                );
            },
        },
        {
            colId: 'description',
            field: 'description',
            headerName: t(TAGSPECS_COL_DESCRIPTION, 'DESCRIPTION'),
            sortable: true,
            flex: 1,
            minWidth: 140,
            cellRenderer: (params: ICellRendererParams<TagSpecDto>) =>
                params.value ? (
                    <div title={params.value}>
                        <Typography
                            variant="body5"
                            color="text-high-emphasis"
                            className="tagspecs-config__desc-text"
                        >
                            {params.value}
                        </Typography>
                    </div>
                ) : (
                    <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                        —
                    </Typography>
                ),
        },
        {
            colId: 'config',
            headerName: t(TAGSPECS_COL_CONFIG, 'CONFIG'),
            sortable: false,
            width: 192,
            minWidth: 100,
            cellRenderer: (params: ICellRendererParams<TagSpecDto>) => {
                const row = params.data;
                if (!row?.config) {
                    return (
                        <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                            {t(TAGSPECS_NO_CONFIG, 'No Config')}
                        </Typography>
                    );
                }
                const keyCount = isJsony(row.config) ? Object.keys(row.config as object).length : 0;
                if (keyCount === 0 && isJsony(row.config)) {
                    return (
                        <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                            {t(TAGSPECS_EMPTY_CONFIG, 'Empty Object')}
                        </Typography>
                    );
                }
                return (
                    <div
                        className="d-flex align-items-center gap-2 text-primary-500"
                        onClick={() => onViewConfig(row)}
                        style={{ cursor: 'pointer' }}
                    >
                        <Typography variant="body5" className="fw-bold">
                            {t(TAGSPECS_VIEW_CONFIG, 'View Config')}
                        </Typography>
                        <span className="badge tagspecs-config__config-key-badge">
                            {keyCount} KEY{keyCount !== 1 ? 'S' : ''}
                        </span>
                    </div>
                );
            },
        },
        {
            colId: 'notify',
            field: 'notify',
            headerName: t(TAGSPECS_COL_NOTIFY, 'NOTIFY'),
            sortable: true,
            width: 96,
            minWidth: 72,
            valueGetter: (p) => (p.data?.notify ? 1 : 0),
            cellRenderer: (params: ICellRendererParams<TagSpecDto>) => (
                <span
                    className={`tagspecs-config__notify-badge tagspecs-config__notify-badge--${
                        params.data?.notify ? 'on' : 'off'
                    }`}
                >
                    {params.data?.notify ? 'ON' : 'OFF'}
                </span>
            ),
        },
        {
            colId: 'createdAt',
            field: 'createdAt',
            headerName: t(TAGSPECS_COL_CREATED, 'CREATED'),
            sortable: true,
            width: 160,
            minWidth: 110,
            cellRenderer: (params: ICellRendererParams<TagSpecDto>) => {
                if (!params.value) {
                    return <Typography variant="body5" color="text-low-emphasis">—</Typography>;
                }
                const date = new Date(params.value);
                return (
                    <div className="d-flex flex-column">
                        <Typography variant="body5" color="text-low-emphasis">
                            {date.toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                            })}
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis">
                            {date.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                            })}
                        </Typography>
                    </div>
                );
            },
        },
        {
            colId: 'ts-actions',
            headerName: '',
            sortable: false,
            width: 96,
            minWidth: 72,
            suppressHeaderMenuButton: true,
            cellRenderer: (params: ICellRendererParams<TagSpecDto>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                return (
                    <div className="d-flex align-items-center gap-3">
                        <ImPencil
                            size={16}
                            className="rules-icon-btn"
                            title={t(TAGSPECS_EDIT_TITLE, 'Edit tag spec')}
                            onClick={() => onEditClickRef.current(row)}
                        />
                        <RiDeleteBin6Line
                            size={16}
                            className="rules-icon-btn rules-icon-btn--danger"
                            title={t(TAGSPECS_DELETE_TITLE, 'Delete tag spec')}
                            onClick={() => onDeleteClickRef.current(row.id)}
                        />
                    </div>
                );
            },
        },
    ];
}
